import { useEffect } from "react";

import { Dialog } from "@fronterahealth/frontera-ui-components";

import {
  ConnectionTypeEnum,
  ProviderRoleEnums,
  useReissueProviderInvitationMutation,
} from "@api/graphql/types-and-hooks";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { getRoleDescription, getRoleLabelFromEnum } from "@pages/Members/utils/providerRoles";

const ASSIGNABLE_ROLES = Object.values(ProviderRoleEnums).filter((role) => role !== ProviderRoleEnums.Intake);

interface SendInviteFormData {
  providerRoles: ProviderRoleEnums[];
  connectionType: ConnectionTypeEnum;
}

interface SendProviderInviteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: string;
  providerEmail: string;
  currentRoles: ProviderRoleEnums[];
  onSent: () => void;
}

export const SendProviderInviteDialog: React.FC<SendProviderInviteDialogProps> = ({
  isOpen,
  onClose,
  providerId,
  providerEmail,
  currentRoles,
  onSent,
}) => {
  const reissueInvitation = useReissueProviderInvitationMutation({
    onSuccess: (res) => {
      if (!res.reissueProviderInvitation?.status) {
        notifyError(res.reissueProviderInvitation?.message || "Something went wrong sending this invite!");
        return;
      }
      notifySuccess(`Invite sent to ${providerEmail}`);
      onSent();
      onClose();
    },
    onError: () => {
      notifyError("Something went wrong sending this invite!");
    },
  });

  const { formState, onSubmit, reset, RegisteredCheckboxList, RegisteredFormRadioInput } =
    useFormUtils<SendInviteFormData>({
      defaultValues: { providerRoles: currentRoles, connectionType: ConnectionTypeEnum.UsernamePassword },
      useNativeValidation: false,
      mutationFn: async (values) => {
        await reissueInvitation.mutateAsync({
          providerId,
          providerRoles: values.providerRoles,
          connectionType: values.connectionType,
        });
      },
    });

  useEffect(() => {
    if (isOpen) {
      reset({ providerRoles: currentRoles, connectionType: ConnectionTypeEnum.UsernamePassword });
    }
  }, [isOpen, currentRoles, reset]);

  return (
    <FormContainer onSubmit={onSubmit}>
      <Dialog
        title="Send invite"
        open={isOpen}
        setOpen={(open) => !open && onClose()}
        primaryButton={{
          text: "Send invite",
          onClick: onSubmit,
          disabled: reissueInvitation.isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: onClose,
        }}
      >
        <div className="flex flex-col gap-4 p-4">
          <RegisteredCheckboxList
            formState={formState}
            formKey="providerRoles"
            title="Roles"
            orientation="horizontal"
            // no defaultChecked: a render-time getValues() read never reflects reset()
            items={ASSIGNABLE_ROLES.map((role) => ({
              id: role,
              title: getRoleLabelFromEnum(role),
              tooltipText: getRoleDescription(role),
            }))}
          />
          <RegisteredFormRadioInput
            formState={formState}
            formKey="connectionType"
            title="Auth0 Account Type"
            items={[
              { id: ConnectionTypeEnum.UsernamePassword, title: "Username & Password" },
              { id: ConnectionTypeEnum.GoogleOauth, title: "Google OAuth" },
            ]}
          />
        </div>
      </Dialog>
    </FormContainer>
  );
};
